import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Users, MapPin, Instagram, Globe, ChevronDown, ChevronRight, Plus, Send, X, Twitter, CheckCircle, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const regionIcons = {
  uk: "🇬🇧",
  north_america: "🌎",
  south_america: "🌎",
  europe: "🇪🇺",
  africa: "🌍",
  asia: "🌏",
  oceania: "🌏"
};

const FanClubCard = ({ club }) => (
  <Link
    to={club.slug ? `/fan-clubs/${club.slug}` : "#"}
    className="block bg-white rounded-lg border border-slate-200 p-4 hover:border-[#7C2C3B] hover:shadow-md transition-all"
    data-testid={`fan-club-${club.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <h3 className="font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
          {club.name.toUpperCase()}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">{club.summary}</p>
        <div className="flex items-center gap-2 flex-wrap text-xs text-[#2BAEE5] font-medium">
          View club page <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </div>
  </Link>
);

const RegionSection = ({ regionKey, regionName, clubs }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  return (
    <div className="mb-8">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-3 w-full text-left mb-4 group"
      >
        <span className="text-3xl">{regionIcons[regionKey]}</span>
        <h2 className="text-2xl font-bold text-[#0F172A] group-hover:text-[#7C2C3B] transition-colors" style={{ fontFamily: 'Barlow Condensed' }}>
          {regionName.toUpperCase()}
        </h2>
        <span className="text-sm text-slate-500 ml-2">({clubs.length} clubs)</span>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-slate-400 ml-auto" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
        )}
      </button>
      
      {isExpanded && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clubs.map(club => (
            <FanClubCard key={club.id} club={club} />
          ))}
        </div>
      )}
    </div>
  );
};

// Submit Fan Club Dialog Component
const SubmitFanClubDialog = ({ open, onOpenChange, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    region: "",
    summary: "",
    website_url: "",
    twitter_handle: "",
    instagram_handle: ""
  });

  const regions = [
    { value: "uk", label: "United Kingdom" },
    { value: "europe", label: "Europe" },
    { value: "north_america", label: "North America" },
    { value: "south_america", label: "South America" },
    { value: "africa", label: "Africa" },
    { value: "asia", label: "Asia" },
    { value: "oceania", label: "Oceania" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.region || !formData.summary) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.summary.length < 20) {
      toast.error("Summary must be at least 20 characters");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/fan-clubs/submit`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fan club submitted for review!");
      setFormData({ name: "", region: "", summary: "", website_url: "", twitter_handle: "", instagram_handle: "" });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit fan club");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
            SUBMIT YOUR FAN CLUB
          </DialogTitle>
          <DialogDescription>
            Know a West Ham fan club that's not listed? Submit it for review and help grow our global Hammers family!
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Fan Club Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Berlin Hammers"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="submit-club-name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="region">Region *</Label>
            <Select
              value={formData.region}
              onValueChange={(value) => setFormData({ ...formData, region: value })}
            >
              <SelectTrigger data-testid="submit-club-region">
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="summary">Description *</Label>
            <Textarea
              id="summary"
              placeholder="Tell us about this fan club (min 20 characters)"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
              required
              data-testid="submit-club-summary"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="website" className="flex items-center gap-2">
              <Globe className="w-4 h-4" /> Website URL
            </Label>
            <Input
              id="website"
              type="url"
              placeholder="https://www.example.com"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              data-testid="submit-club-website"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="twitter" className="flex items-center gap-2">
                <Twitter className="w-4 h-4" /> Twitter Handle
              </Label>
              <Input
                id="twitter"
                placeholder="@username"
                value={formData.twitter_handle}
                onChange={(e) => setFormData({ ...formData, twitter_handle: e.target.value })}
                data-testid="submit-club-twitter"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="instagram" className="flex items-center gap-2">
                <Instagram className="w-4 h-4" /> Instagram Handle
              </Label>
              <Input
                id="instagram"
                placeholder="@username"
                value={formData.instagram_handle}
                onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                data-testid="submit-club-instagram"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              disabled={loading}
              data-testid="submit-club-button"
            >
              {loading ? "Submitting..." : "Submit for Review"}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Submit Fan Club Card Component
const SubmitFanClubCard = ({ onClick }) => (
  <div 
    onClick={onClick}
    className="bg-gradient-to-br from-[#2BAEE5] to-[#1a8fc4] rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-all group"
    data-testid="submit-fan-club-card"
  >
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
        <Plus className="w-7 h-7" />
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
          KNOW A FAN CLUB?
        </h3>
        <p className="text-white/80 text-sm mb-3">
          Help us grow the global Hammers community! Submit a fan club you know for our admins to review.
        </p>
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>Submit a Fan Club</span>
          <Plus className="w-4 h-4" />
        </div>
      </div>
    </div>
  </div>
);

export default function FanClubsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ regions: {}, clubs: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);

  useEffect(() => {
    fetchFanClubs();
    if (user) {
      fetchMySubmissions();
    }
  }, [user]);

  const fetchFanClubs = async () => {
    try {
      const response = await axios.get(`${API}/fan-clubs`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch fan clubs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubmissions = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API}/fan-clubs/submissions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySubmissions(response.data.submissions || []);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    }
  };

  const regionOrder = ["uk", "europe", "north_america", "south_america", "africa", "asia", "oceania"];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fan-clubs-page">
      <SEO title="Fan Clubs" path="/fan-clubs" description="Join a West Ham United supporters club near you. Find local fan groups, away travel clubs and community groups for Hammers fans worldwide." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#F8D742_0%,transparent_70%)]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-2">
            <Users className="w-10 h-10 text-[#F8D742]" />
            <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              HAMMERS FAN CLUBS
            </h1>
          </div>
          <p className="text-white/80">West Ham United supporters communities around the world</p>
        </div>
      </div>

      {/* Know A Fan Club? - Clickable Ribbon at Top */}
      <div 
        onClick={() => {
          if (!user) {
            toast.error("Please log in to submit a fan club");
            return;
          }
          setShowSubmitDialog(true);
        }}
        className="bg-gradient-to-r from-[#2BAEE5] to-[#1a8fc4] cursor-pointer hover:from-[#1a8fc4] hover:to-[#157ba3] transition-all"
        data-testid="submit-fan-club-ribbon"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-3">
          <Users className="w-5 h-5 text-white" />
          <span className="text-white font-bold text-sm md:text-base" style={{ fontFamily: 'Barlow Condensed' }}>
            KNOW A FAN CLUB? SUBMIT IT HERE
          </span>
          <Plus className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-center">
            <div className="p-3 rounded-lg bg-slate-50">
              <div className="text-2xl font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>
                {data.total}
              </div>
              <div className="text-xs text-slate-500">Total Clubs</div>
            </div>
            {regionOrder.map(region => (
              <div key={region} className="p-3 rounded-lg bg-slate-50">
                <div className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                  {data.clubs[region]?.length || 0}
                </div>
                <div className="text-xs text-slate-500">{regionIcons[region]} {data.regions[region]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Region Filter Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white px-4 py-2"
              >
                <Globe className="w-4 h-4 mr-2" />
                All Regions
              </TabsTrigger>
              {regionOrder.map(region => (
                <TabsTrigger
                  key={region}
                  value={region}
                  className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white px-3 py-2"
                >
                  <span className="mr-2">{regionIcons[region]}</span>
                  {data.regions[region]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#7C2C3B] border-t-transparent"></div>
          </div>
        ) : data.total === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              NO FAN CLUBS YET
            </h3>
            <p className="text-slate-500">Fan club data will be added soon.</p>
          </div>
        ) : activeTab === "all" ? (
          // Show all regions
          <div>
            {regionOrder.map(region => {
              const clubs = data.clubs[region] || [];
              if (clubs.length === 0) return null;
              return (
                <RegionSection
                  key={region}
                  regionKey={region}
                  regionName={data.regions[region]}
                  clubs={clubs}
                />
              );
            })}
          </div>
        ) : (
          // Show single region
          <RegionSection
            regionKey={activeTab}
            regionName={data.regions[activeTab]}
            clubs={data.clubs[activeTab] || []}
          />
        )}

        {/* Global Community Message */}
        <div className="mt-12 bg-gradient-to-r from-[#7C2C3B] to-[#5a1f2b] rounded-xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
            FOREVER BLOWING BUBBLES AROUND THE WORLD
          </h3>
          <p className="text-white/80 max-w-2xl mx-auto">
            West Ham United's global family spans {Object.keys(data.regions).length} continents with {data.total} official 
            and unofficial supporters groups. Whether you're in London, Lagos, Los Angeles or anywhere in between, 
            there's a Hammers community waiting for you. COYI! ⚒️
          </p>
        </div>

        {/* Submit Fan Club Section */}
        <div className="mt-12">
          <div className="grid md:grid-cols-1 gap-6">
            {/* My Submissions Card (only shown if user has submissions) */}
            {user && mySubmissions.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  YOUR SUBMISSIONS
                </h3>
                <div className="space-y-3">
                  {mySubmissions.slice(0, 3).map(sub => (
                    <div key={sub.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <div className="font-medium text-[#0F172A]">{sub.name}</div>
                        <div className="text-xs text-slate-500">{sub.region.replace('_', ' ').toUpperCase()}</div>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        sub.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {sub.status === 'pending' && <Clock className="w-3 h-3" />}
                        {sub.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                        {sub.status === 'rejected' && <X className="w-3 h-3" />}
                        {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                      </div>
                    </div>
                  ))}
                  {mySubmissions.length > 3 && (
                    <p className="text-xs text-slate-500 text-center">
                      + {mySubmissions.length - 3} more submissions
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Fan Club Dialog */}
      <SubmitFanClubDialog 
        open={showSubmitDialog} 
        onOpenChange={setShowSubmitDialog}
        onSuccess={fetchMySubmissions}
      />

      <Footer />
    </div>
  );
}
